/**
 * The write half of the n8n tab: power state, vCPU/memory, and growing a disk, each sent straight
 * to the WiService webhook.
 *
 * Everything here changes a live customer machine and NOTHING here updates WiCloud's record — the
 * resource keeps its stored hardware, price, billing cycle and expiry. That is the whole reason the
 * panel leads with a warning and follows a successful specs or disk change with the prompt that
 * sends the operator to Compare configuration. An operator who wants the bill to follow the change
 * wants the ordinary Change-configuration flow instead, not this.
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  Cpu,
  HardDrive,
  Loader2,
  Power,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

import { adminResourceService } from "@/services/admin-resource.service";
import {
  N8N_POWER_ACTIONS,
  type N8nPowerAction,
  type N8nVmActionResult,
} from "@/types/admin";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BusyOverlay,
  PROVIDER_WAIT_HINT,
} from "@/components/common/BusyOverlay";

interface N8nVmActionsPanelProps {
  resourceId: string;
  resourceName: string;
}

/**
 * What each power action really does to a running machine. Written out because `stop` and
 * `shutdown` look interchangeable in a dropdown and are not: one asks the guest OS, the other pulls
 * the power.
 */
const POWER_ACTION_HELP: Record<N8nPowerAction, string> = {
  start: "Start a stopped machine.",
  stop: "Cut the power — like unplugging it. Unwritten data can be lost.",
  shutdown:
    "Ask the guest OS to shut down. The OS can refuse or take its time.",
  reboot: "Ask the guest OS to restart.",
  suspend: "Pause the machine. It still reports 'running' to the provider.",
  resume: "Continue a suspended machine.",
  reset: "Force a restart — like the reset button. Unwritten data can be lost.",
};

/** The two actions that cut power without asking the guest OS. */
const DESTRUCTIVE_POWER_ACTIONS: N8nPowerAction[] = ["stop", "reset"];

export function N8nVmActionsPanel({
  resourceId,
  resourceName,
}: N8nVmActionsPanelProps) {
  const [powerAction, setPowerAction] = useState<N8nPowerAction>("shutdown");
  const [confirmPowerOpen, setConfirmPowerOpen] = useState(false);

  const [cores, setCores] = useState("");
  const [memoryMb, setMemoryMb] = useState("");
  const [confirmSpecsOpen, setConfirmSpecsOpen] = useState(false);

  const [diskName, setDiskName] = useState("");
  const [growByGb, setGrowByGb] = useState("");
  const [confirmDiskOpen, setConfirmDiskOpen] = useState(false);

  const [result, setResult] = useState<N8nVmActionResult | null>(null);

  const announce = (data: N8nVmActionResult) => {
    setResult(data);
    toast.success(`${data.summary} — accepted by the infrastructure.`);
  };

  const fail = (error: Error, fallback: string) => {
    setResult(null);
    toast.error(error.message || fallback);
  };

  const powerMutation = useMutation({
    mutationFn: () =>
      adminResourceService.runN8nPowerAction(resourceId, powerAction),
    onSuccess: announce,
    onError: (error: Error) => fail(error, "The power action failed."),
  });

  // Sent only when filled in: the API takes either figure on its own, and an empty box means
  // "leave this half alone" rather than "set it to zero".
  const parsedCores = cores.trim() === "" ? undefined : Number(cores);
  const parsedMemory = memoryMb.trim() === "" ? undefined : Number(memoryMb);
  const specsUsable =
    (parsedCores !== undefined &&
      Number.isInteger(parsedCores) &&
      parsedCores > 0) ||
    (parsedMemory !== undefined &&
      Number.isInteger(parsedMemory) &&
      parsedMemory > 0);

  const specsMutation = useMutation({
    mutationFn: () =>
      adminResourceService.runN8nSpecsAction(resourceId, {
        cores: parsedCores,
        memoryMb: parsedMemory,
      }),
    onSuccess: announce,
    onError: (error: Error) => fail(error, "The vCPU/memory change failed."),
  });

  const parsedGrowth = growByGb.trim() === "" ? NaN : Number(growByGb);
  const diskUsable =
    diskName.trim() !== "" &&
    Number.isInteger(parsedGrowth) &&
    parsedGrowth > 0;

  const diskMutation = useMutation({
    mutationFn: () =>
      adminResourceService.runN8nDiskAction(resourceId, {
        diskName: diskName.trim(),
        growByGb: parsedGrowth,
      }),
    onSuccess: announce,
    onError: (error: Error) => fail(error, "The disk could not be grown."),
  });

  const busy =
    powerMutation.isPending ||
    specsMutation.isPending ||
    diskMutation.isPending;

  // Every write here lands on a live machine and none of them can be taken back — a grown disk never
  // shrinks. So the page is covered while one is out, the confirm that started it stays up until it
  // answers, and the fields are frozen. The pending flags live in this panel's own mutations, which
  // is exactly why the veil matters: switching tab or endpoint unmounts the panel, and on return a
  // fresh `busy` of false would have offered "Grow" again with the first request still in flight.
  const busyLabel = powerMutation.isPending
    ? `Sending '${powerAction}' to the machine…`
    : specsMutation.isPending
      ? "Changing vCPU and memory…"
      : diskMutation.isPending
        ? "Growing the disk…"
        : "";

  /**
   * Shared shape of the three confirms: only the request closes one while it runs (Escape, an
   * outside click and Cancel are all held), and it closes once the request settles either way — the
   * toast and the result card carry the outcome from there.
   */
  const guardedOpenChange =
    (setOpen: (open: boolean) => void) => (open: boolean) => {
      if (open || !busy) setOpen(open);
    };

  const blockEscapeWhileBusy = (event: KeyboardEvent) => {
    if (busy) {
      event.preventDefault();
    }
  };

  return (
    <>
      <div className="space-y-5">
        <Alert className="border-destructive/30 bg-destructive-soft">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertTitle className="text-destructive">
            These go straight to the machine
          </AlertTitle>
          <AlertDescription className="text-foreground">
            <p className="mb-2">
              Every action here changes {resourceName}'s real VM immediately,
              and WiCloud's record is <span className="font-medium">not</span>{" "}
              updated: its stored vCPU, memory, disks, price, billing cycle and
              expiry all stay as they are, so the customer goes on being charged
              for the old configuration.
            </p>
            <p>
              After a vCPU/memory or disk change, reconcile the record from{" "}
              <span className="font-medium">Compare configuration</span>. To
              change what the customer is billed for, use{" "}
              <span className="font-medium">Change configuration</span> instead
              — not this panel.
            </p>
          </AlertDescription>
        </Alert>

        {result && (
          <Alert
            className={
              result.recordNeedsReconciling
                ? "border-destructive/30 bg-destructive-soft"
                : undefined
            }
          >
            <Zap
              className={`h-4 w-4 ${result.recordNeedsReconciling ? "text-destructive" : ""}`}
            />
            <AlertTitle>{result.summary}</AlertTitle>
            <AlertDescription className="text-foreground">
              <p>
                {result.providerMessage || "Accepted by the infrastructure."}
                {result.executionId !== null && (
                  <>
                    {" "}
                    n8n execution{" "}
                    <span className="font-mono">{result.executionId}</span>.
                  </>
                )}
              </p>
              {result.recordNeedsReconciling && (
                <p className="mt-2 font-medium">
                  WiCloud's record now disagrees with the machine. Open Compare
                  configuration and resolve the mismatch.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-border/80 shadow-none">
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Power className="h-4 w-4" />
              Power state
            </CardTitle>
            <CardDescription>
              Calls <span className="font-mono">POST cloud-vm/state</span>{" "}
              directly. The resource's own status is left alone — the status
              sync picks the change up by itself.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* The help line sits under the whole row, not inside the select's
                column — inside it, `items-end` would align Run to the bottom of
                the text instead of the select. */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="n8n-power-action">Action</Label>
                <Select
                  value={powerAction}
                  onValueChange={(value) =>
                    setPowerAction(value as N8nPowerAction)
                  }
                  disabled={busy}
                >
                  <SelectTrigger
                    id="n8n-power-action"
                    className="cursor-pointer"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {N8N_POWER_ACTIONS.map((action) => (
                      <SelectItem
                        key={action}
                        value={action}
                        className="cursor-pointer"
                      >
                        {action}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant={
                  DESTRUCTIVE_POWER_ACTIONS.includes(powerAction)
                    ? "destructive"
                    : "outline"
                }
                disabled={busy}
                onClick={() => setConfirmPowerOpen(true)}
                className="w-full sm:w-auto"
              >
                {powerMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Run
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {POWER_ACTION_HELP[powerAction]}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-none">
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Cpu className="h-4 w-4" />
              vCPU and memory
            </CardTitle>
            <CardDescription>
              Calls <span className="font-mono">PATCH cloud-vm</span>. Both
              figures are totals, not amounts to add — leave one blank to leave
              it alone. The webhook only runs on a{" "}
              <span className="font-medium">stopped</span> VM, and a suspended
              one does not count as stopped.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="n8n-cores">vCPU (total)</Label>
              <Input
                id="n8n-cores"
                inputMode="numeric"
                placeholder="unchanged"
                value={cores}
                onChange={(event) => setCores(event.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label htmlFor="n8n-memory">Memory MB (total)</Label>
              <Input
                id="n8n-memory"
                inputMode="numeric"
                placeholder="unchanged"
                value={memoryMb}
                onChange={(event) => setMemoryMb(event.target.value)}
                disabled={busy}
              />
            </div>
            <Button
              variant="destructive"
              disabled={busy || !specsUsable}
              onClick={() => setConfirmSpecsOpen(true)}
              className="w-full sm:w-auto"
            >
              {specsMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Apply
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-none">
          <CardHeader className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-4 w-4" />
              Grow a disk
            </CardTitle>
            <CardDescription>
              Calls <span className="font-mono">PATCH cloud-vm/disk</span>,
              whose <span className="font-mono">size</span> field is capacity to{" "}
              <span className="font-medium">add</span>. A virtual disk grows and
              never shrinks, so this cannot be undone. The device name is
              checked against the machine's real disks first — use the{" "}
              <span className="font-medium">Disks</span> probe in this menu to
              read them.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="n8n-disk-name">Device</Label>
              <Input
                id="n8n-disk-name"
                placeholder="scsi0"
                value={diskName}
                onChange={(event) => setDiskName(event.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label htmlFor="n8n-disk-growth">Add (GB)</Label>
              <Input
                id="n8n-disk-growth"
                inputMode="numeric"
                placeholder="50"
                value={growByGb}
                onChange={(event) => setGrowByGb(event.target.value)}
                disabled={busy}
              />
            </div>
            <Button
              variant="destructive"
              disabled={busy || !diskUsable}
              onClick={() => setConfirmDiskOpen(true)}
              className="w-full sm:w-auto"
            >
              {diskMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Grow
            </Button>
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={confirmPowerOpen}
        onOpenChange={guardedOpenChange(setConfirmPowerOpen)}
      >
        <AlertDialogContent onEscapeKeyDown={blockEscapeWhileBusy}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send '{powerAction}' to {resourceName}?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {POWER_ACTION_HELP[powerAction]} This is a live customer machine.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={busy}
              onClick={(event) => {
                // Radix closes on click; held open until the request settles instead.
                event.preventDefault();
                powerMutation.mutate(undefined, {
                  onSettled: () => setConfirmPowerOpen(false),
                });
              }}
            >
              {powerMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Run '{powerAction}'
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmSpecsOpen}
        onOpenChange={guardedOpenChange(setConfirmSpecsOpen)}
      >
        <AlertDialogContent onEscapeKeyDown={blockEscapeWhileBusy}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change {resourceName}'s hardware?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  {parsedCores !== undefined && (
                    <>vCPU becomes {parsedCores}. </>
                  )}
                  {parsedMemory !== undefined && (
                    <>Memory becomes {parsedMemory} MB. </>
                  )}
                  The machine must already be stopped, or the infrastructure
                  will refuse.
                </p>
                <p className="font-medium text-foreground">
                  The customer's bill does not change. Reconcile the record from
                  Compare configuration afterwards.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                specsMutation.mutate(undefined, {
                  onSettled: () => setConfirmSpecsOpen(false),
                });
              }}
            >
              {specsMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmDiskOpen}
        onOpenChange={guardedOpenChange(setConfirmDiskOpen)}
      >
        <AlertDialogContent onEscapeKeyDown={blockEscapeWhileBusy}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Add {parsedGrowth} GB to {diskName.trim()}?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">
                  This adds {parsedGrowth} GB to the device — it does not set
                  its size to {parsedGrowth} GB. A virtual disk cannot be shrunk
                  again, so there is no way back.
                </p>
                <p className="font-medium text-foreground">
                  The customer's bill does not change. Reconcile the record from
                  Compare configuration afterwards.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                diskMutation.mutate(undefined, {
                  onSettled: () => setConfirmDiskOpen(false),
                });
              }}
            >
              {diskMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Grow disk
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BusyOverlay open={busy} label={busyLabel} hint={PROVIDER_WAIT_HINT} />
    </>
  );
}
